/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentOwnershipGraph.module.scss';

type Translate = (key: string) => string;

type FilterKey = 'all' | 'sub' | 'mgmt' | 'share' | 'mon';

const getFilters = (t: Translate): { key: FilterKey; label: string }[] => [
  { key: 'all', label: t('All') },
  { key: 'sub', label: t('Subsidiaries') },
  { key: 'mgmt', label: t('Management') },
  { key: 'share', label: t('Shareholders') },
  { key: 'mon', label: t('Monitoring') },
];

const flag = (code: string) => `/storage/flags/${code}.svg`;

type MonRow = {
  date: string;
  code: string;
  entity: string;
  change: string;
  consequence: string;
  tag: string;
  tagTone: 's' | 'o' | 'p' | 'b';
  crit?: boolean;
};

const getMonRows = (t: Translate): MonRow[] => [
  {
    date: '2026-07-27',
    code: 'ae',
    entity: 'D. MARLOWE',
    change: 'Added to a consolidated designation list. Holds 68.00, 80.00 and 55.00 of the three shareholder vehicles.',
    consequence:
      'Aggregate ownership by a designated person now 59.90 — above the 50.00 test. Escalate before the next payment run.',
    tag: t('Sanctions'),
    tagTone: 's',
    crit: true,
  },
  {
    date: '2026-07-19',
    code: 'ch',
    entity: 'SAMPLE ALPINE TRUST SA',
    change: 'Shareholding in the bidder moved 15.00 to 18.00 on a filed shareholder list.',
    consequence: 'This filing is what carried the aggregate across the threshold. Three points, one register, one day.',
    tag: t('Ownership'),
    tagTone: 'o',
    crit: true,
  },
  {
    date: '2026-06-14',
    code: 'gb',
    entity: 'A. VEIGA',
    change: 'Appointed director of the bidder, recorded at Companies House.',
    consequence: 'Politically exposed person newly holding office on an active contract.',
    tag: t('PEP'),
    tagTone: 'p',
  },
  {
    date: '2026-05-02',
    code: 'ae',
    entity: 'SAMPLE GULF TRADING FZE',
    change: 'Entered the chain as a 20.00 shareholder of the bidder.',
    consequence: 'A new intermediate layer in a third jurisdiction, six weeks after award.',
    tag: t('Structure'),
    tagTone: 'o',
  },
  {
    date: '2026-03-12',
    code: 'gb',
    entity: 'SAMPLE MARINE LOGISTICS LTD',
    change: 'Contract awarded. Ownership chain resolved clean, no list matches at any node.',
    consequence:
      'Everything above happened after this point. Due diligence at award is a photograph; the graph is a film.',
    tag: t('Baseline'),
    tagTone: 'b',
  },
];

const SIDE_ICONS = {
  sources: <path d="M4 10h16M6 10v9M10 10v9M14 10v9M18 10v9M5 19h14M12 3l8 5H4z" />,
  depth: (
    <>
      <path d="M12 3.5 21 8l-9 4.5L3 8z" />
      <path d="M3 13l9 4.5 9-4.5" />
      <path d="M3 17.5 12 22l9-4.5" />
    </>
  ),
  screening: (
    <>
      <path d="M12 3.5 19.5 6.5V12c0 4.6-3.1 7.9-7.5 9-4.4-1.1-7.5-4.4-7.5-9V6.5z" />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  output: (
    <>
      <path d="M5 12h13M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 19h13" strokeLinecap="round" />
    </>
  ),
} as const;

const getSide = (t: Translate): { k: string; p: string; icon: keyof typeof SIDE_ICONS }[] => [
  {
    k: t('Sources'),
    p: 'Companies House, KVK Handelsregister, UAE free-zone registry, Registre du commerce',
    icon: 'sources',
  },
  { k: t('Depth'), p: t('Resolved to ultimate beneficial owner across three intermediate layers'), icon: 'depth' },
  {
    k: t('Screening'),
    p: t('Every node in the graph screened against consolidated sanctions and PEP data, synced daily'),
    icon: 'screening',
  },
  { k: t('Output'), p: t('API, bulk file, or an ownership graph your analyst can open and cite'), icon: 'output' },
];

export const GovernmentOwnershipGraph = () => {
  const { t } = useInt();
  const filters = getFilters(t);
  const side = getSide(t);
  const monRows = getMonRows(t);
  const [filter, setFilter] = useState<FilterKey>('all');
  const canvasRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(canvasRef, { threshold: 0.15 });
  const sideRef = useRef<HTMLDivElement>(null);
  const sideRevealed = useIntersection(sideRef, { threshold: 0.2 });

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('No single holding breaches the threshold. Together, they do.')}
          description={t(
            'A designated person rarely holds a majority stake in their own name. They hold minority positions through several vehicles in several jurisdictions, each individually below the line. Screening the bidder against a list returns clean. Resolving the graph does not.',
          )}
          align="left"
        />
      </div>

      <div className={styles.ogx}>
        <div className={styles.ogxBar}>
          <div className={styles.ogxSeg}>
            {filters.map((f) => (
              <button
                type="button"
                className={cn(styles.ogxSegBtn, filter === f.key && styles.on)}
                onClick={() => setFilter(f.key)}
                key={f.key}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.ogxCanvas} ref={canvasRef}>
          {filter !== 'mon' ? (
            <>
              <div className={styles.ogxLegend}>
                <span>{t('Legend')}</span>
                <span className={styles.legOwns}>● {t('Owns')}</span>
                <span className={styles.legOfficer}>● {t('Officer')}</span>
                <span className={styles.legSanctioned}>● {t('Sanctioned')}</span>
                <span className={styles.legPep}>● {t('PEP')}</span>
              </div>
              <div className={styles.ogxStats}>
                <span>
                  <b>6</b>
                  {t('Companies')}
                </span>
                <span>
                  <b>3</b>
                  {t('Officers')}
                </span>
                <span className={styles.statR}>
                  <b>1</b>
                  {t('Sanctioned')}
                </span>
                <span className={styles.statA}>
                  <b>1</b>
                  {t('PEP')}
                </span>
              </div>

              {revealed && (
                <svg
                  className={cn(styles.gsvg, styles[`f_${filter}`])}
                  viewBox="0 0 1240 740"
                  xmlns="http://www.w3.org/2000/svg"
                  role="img"
                  aria-label="Ownership graph of SAMPLE MARINE LOGISTICS LTD showing three shareholder vehicles all owned by a sanctioned person, two subsidiaries, and a politically exposed director"
                >
                  <defs>
                    <marker
                      id="gov-mg"
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M0 0 L10 5 L0 10z" fill="#17935A" />
                    </marker>
                    <marker
                      id="gov-mr"
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M0 0 L10 5 L0 10z" fill="#DC2626" />
                    </marker>
                    <marker
                      id="gov-ma"
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M0 0 L10 5 L0 10z" fill="#B26F0E" />
                    </marker>
                  </defs>

                  <g className={cn(styles.grp, styles.gShare)}>
                    <g fill="none" stroke="#DC2626" strokeWidth="1.7" markerEnd="url(#gov-mr)">
                      <path
                        className={styles.gedge}
                        style={{ animationDelay: '.15s' }}
                        d="M138 340 C 220 300 250 150 330 150"
                      />
                      <path className={styles.gedge} style={{ animationDelay: '.25s' }} d="M140 360 H 270" />
                      <path
                        className={styles.gedge}
                        style={{ animationDelay: '.35s' }}
                        d="M138 380 C 220 420 250 570 330 570"
                      />
                    </g>
                    <g fill="none" stroke="#17935A" strokeWidth="1.7" markerEnd="url(#gov-mg)">
                      <path
                        className={styles.gedge}
                        style={{ animationDelay: '.55s' }}
                        d="M390 150 C 500 150 520 330 582 342"
                      />
                      <path className={styles.gedge} style={{ animationDelay: '.6s' }} d="M330 360 H 582" />
                      <path
                        className={styles.gedge}
                        style={{ animationDelay: '.65s' }}
                        d="M390 570 C 500 570 520 390 582 378"
                      />
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.4s' }}>
                      <rect
                        x="334"
                        y="130"
                        width="52"
                        height="40"
                        rx="9"
                        fill="#fff"
                        stroke="#3BBE7E"
                        strokeWidth="1.6"
                      />
                      <image href={flag('nl')} x="340" y="136" width="40" height="28" />
                      <rect x="272" y="178" width="176" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="360"
                        y="193"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        SAMPLE NORTH HOLDINGS
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.48s' }}>
                      <rect
                        x="274"
                        y="340"
                        width="52"
                        height="40"
                        rx="9"
                        fill="#fff"
                        stroke="#3BBE7E"
                        strokeWidth="1.6"
                      />
                      <image href={flag('ae')} x="280" y="346" width="40" height="28" />
                      <rect x="221" y="388" width="158" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="300"
                        y="403"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        SAMPLE GULF TRADING
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.56s' }}>
                      <rect
                        x="334"
                        y="550"
                        width="52"
                        height="40"
                        rx="9"
                        fill="#fff"
                        stroke="#3BBE7E"
                        strokeWidth="1.6"
                      />
                      <image href={flag('ch')} x="340" y="556" width="40" height="28" />
                      <rect x="281" y="598" width="158" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="360"
                        y="613"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        SAMPLE ALPINE TRUST
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.1s' }}>
                      <circle cx="105" cy="360" r="24" fill="rgba(220,38,38,.12)" stroke="#DC2626" strokeWidth="1.6" />
                      <path
                        d="M105 356 a6 6 0 1 1 .1 0 M94 375 a11 10 0 0 1 22 0"
                        fill="none"
                        stroke="#DC2626"
                        strokeWidth="1.4"
                      />
                      <rect x="50" y="392" width="110" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="105"
                        y="407"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        D. MARLOWE
                      </text>
                      <rect x="56" y="418" width="98" height="21" rx="5" fill="rgba(220,38,38,.10)" stroke="#DC2626" />
                      <text
                        x="105"
                        y="432.5"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10"
                        letterSpacing="1.1"
                        fill="#DC2626"
                      >
                        SANCTIONED
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.7s' }}>
                      <rect x="182" y="228" width="92" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="228"
                        y="242"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 68.00
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.74s' }}>
                      <rect x="154" y="326" width="92" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="200"
                        y="340"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 80.00
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.78s' }}>
                      <rect x="182" y="476" width="92" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="228"
                        y="490"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 55.00
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.82s' }}>
                      <rect x="459" y="222" width="92" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="505"
                        y="236"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 50.00
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.86s' }}>
                      <rect x="424" y="326" width="92" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="470"
                        y="340"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 20.00
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.9s' }}>
                      <rect x="459" y="480" width="92" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="505"
                        y="494"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 18.00
                      </text>
                    </g>
                  </g>

                  <g className={cn(styles.grp, styles.gSub)}>
                    <g fill="none" stroke="#17935A" strokeWidth="1.7" markerEnd="url(#gov-mg)">
                      <path
                        className={styles.gedge}
                        style={{ animationDelay: '.8s' }}
                        d="M658 344 C 780 300 810 175 890 175"
                      />
                      <path
                        className={styles.gedge}
                        style={{ animationDelay: '.85s' }}
                        d="M658 376 C 780 420 810 545 890 545"
                      />
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.9s' }}>
                      <rect
                        x="894"
                        y="155"
                        width="52"
                        height="40"
                        rx="9"
                        fill="#fff"
                        stroke="#3BBE7E"
                        strokeWidth="1.6"
                      />
                      <image href={flag('ie')} x="900" y="161" width="40" height="28" />
                      <rect x="853" y="203" width="134" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="920"
                        y="218"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        SAMPLE MARINE (IE)
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '0.96s' }}>
                      <rect
                        x="894"
                        y="525"
                        width="52"
                        height="40"
                        rx="9"
                        fill="#fff"
                        stroke="#3BBE7E"
                        strokeWidth="1.6"
                      />
                      <image href={flag('lu')} x="900" y="531" width="40" height="28" />
                      <rect x="837" y="573" width="166" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="920"
                        y="588"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        SAMPLE PORT SERVICES
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.0s' }}>
                      <rect x="745" y="222" width="100" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="795"
                        y="236"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 100.00
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.05s' }}>
                      <rect x="745" y="476" width="100" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="795"
                        y="490"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Owns 100.00
                      </text>
                    </g>
                  </g>

                  <g className={cn(styles.grp, styles.gMgmt)}>
                    <g fill="none" stroke="#B26F0E" strokeWidth="1.6" strokeDasharray="5 4" markerEnd="url(#gov-ma)">
                      <path className={styles.gedge} style={{ animationDelay: '1.0s' }} d="M876 360 H 660" />
                    </g>
                    <g fill="none" stroke="#9AA3B8" strokeWidth="1.4" strokeDasharray="5 4">
                      <path className={styles.gedge} style={{ animationDelay: '1.05s' }} d="M620 634 V 447" />
                      <path className={styles.gedge} style={{ animationDelay: '1.1s' }} d="M620 128 V 326" />
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.1s' }}>
                      <circle cx="900" cy="360" r="24" fill="rgba(178,111,14,.14)" stroke="#B26F0E" strokeWidth="1.6" />
                      <path
                        d="M900 356 a6 6 0 1 1 .1 0 M889 375 a11 10 0 0 1 22 0"
                        fill="none"
                        stroke="#B26F0E"
                        strokeWidth="1.4"
                      />
                      <rect x="856" y="392" width="88" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="900"
                        y="407"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        A. VEIGA
                      </text>
                      <rect
                        x="875"
                        y="418"
                        width="50"
                        height="21"
                        rx="5"
                        fill="rgba(178,111,14,.10)"
                        stroke="#B26F0E"
                      />
                      <text
                        x="900"
                        y="432.5"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10"
                        letterSpacing="1.1"
                        fill="#B26F0E"
                      >
                        PEP
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.15s' }}>
                      <circle
                        cx="620"
                        cy="658"
                        r="24"
                        fill="rgba(139,123,216,.16)"
                        stroke="#8B7BD8"
                        strokeWidth="1.6"
                      />
                      <path
                        d="M620 654 a6 6 0 1 1 .1 0 M609 673 a11 10 0 0 1 22 0"
                        fill="none"
                        stroke="#8B7BD8"
                        strokeWidth="1.4"
                      />
                      <rect x="566" y="690" width="108" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="620"
                        y="705"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        J. OKONKWO
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.2s' }}>
                      <circle
                        cx="620"
                        cy="104"
                        r="24"
                        fill="rgba(139,123,216,.16)"
                        stroke="#8B7BD8"
                        strokeWidth="1.6"
                      />
                      <path
                        d="M620 100 a6 6 0 1 1 .1 0 M609 119 a11 10 0 0 1 22 0"
                        fill="none"
                        stroke="#8B7BD8"
                        strokeWidth="1.4"
                      />
                      <rect x="563" y="136" width="114" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                      <text
                        x="620"
                        y="151"
                        textAnchor="middle"
                        fontFamily="'Inter',sans-serif"
                        fontSize="11.5"
                        fontWeight="600"
                        fill="#0E0F1A"
                      >
                        M. LINDQVIST
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.25s' }}>
                      <rect x="748" y="326" width="72" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="784"
                        y="340"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Director
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.28s' }}>
                      <rect x="584" y="510" width="72" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="620"
                        y="524"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Director
                      </text>
                    </g>
                    <g className={styles.gnode} style={{ animationDelay: '1.31s' }}>
                      <rect x="572" y="214" width="96" height="20" rx="5" fill="#fff" stroke="rgba(14,15,26,.14)" />
                      <text
                        x="620"
                        y="228"
                        textAnchor="middle"
                        fontFamily="'IBM Plex Mono',monospace"
                        fontSize="10.5"
                        fill="#454A5E"
                      >
                        Co. Secretary
                      </text>
                    </g>
                  </g>

                  <g className={styles.gnode} style={{ animationDelay: '.05s' }}>
                    <rect
                      x="586"
                      y="330"
                      width="68"
                      height="52"
                      rx="11"
                      fill="#fff"
                      stroke="#17935A"
                      strokeWidth="2.2"
                    />
                    <image href={flag('gb')} x="594" y="340" width="52" height="34" />
                    <rect x="511" y="392" width="218" height="22" rx="6" fill="#fff" stroke="rgba(14,15,26,.16)" />
                    <text
                      x="620"
                      y="407"
                      textAnchor="middle"
                      fontFamily="'Inter',sans-serif"
                      fontSize="12"
                      fontWeight="700"
                      fill="#0E0F1A"
                    >
                      SAMPLE MARINE LOGISTICS LTD
                    </text>
                    <rect x="529" y="418" width="182" height="21" rx="5" fill="rgba(23,147,90,.10)" stroke="#17935A" />
                    <text
                      x="620"
                      y="432.5"
                      textAnchor="middle"
                      fontFamily="'IBM Plex Mono',monospace"
                      fontSize="10"
                      letterSpacing=".9"
                      fill="#17935A"
                    >
                      THE BIDDER · SCREENS CLEAN
                    </text>
                  </g>
                </svg>
              )}
            </>
          ) : (
            <div className={styles.ogxMon}>
              <div className={styles.monHead}>
                <span className={styles.monH}>{t('Changes since contract award')}</span>
                <span className={styles.monS}>baseline 12 Mar 2026 · 6 entities · 3 officers</span>
                <span className={styles.monLive}>
                  <i />
                  {t('Monitoring active')}
                </span>
              </div>

              {monRows.map((row) => (
                <div
                  className={cn(styles.monRow, row.crit && styles.monRowCrit, row.tagTone === 'b' && styles.monRowBase)}
                  key={row.date}
                >
                  <span className={styles.monDt}>{row.date}</span>
                  <Flag code={row.code} className={styles.monFlag} />
                  <span className={styles.monBody}>
                    <span className={styles.monEn}>{row.entity}</span>
                    <span className={styles.monCh}>{row.change}</span>
                    <span className={styles.monCq}>{row.consequence}</span>
                  </span>
                  <span className={cn(styles.monTag, styles[`tag_${row.tagTone}`])}>{row.tag}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={cn(styles.side, sideRevealed && styles.sideOn)} ref={sideRef}>
        {side.map((item, i) => (
          <div className={styles.sideCard} style={{ '--d': `${i * 70}ms` } as CSSProperties} key={item.k}>
            <span className={styles.sideIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {SIDE_ICONS[item.icon]}
              </svg>
            </span>
            <div className={styles.sideK}>{item.k}</div>
            <p>{item.p}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};
